/**
 * "forget it now" — the data right for a person with no account.
 *
 * The Trust Center already tells a visitor that a conversation held here
 * without an account is kept for thirty days. Until now that sentence had no
 * verb attached to it: a signed-in person could download everything and ask
 * for deletion, and the one surface where somebody has no account to sign
 * into had neither. This is the button that fixes that, and it sits directly
 * under the sentence that explains why anybody would want it — the right
 * moment, rather than a control buried in a settings page a visitor does not
 * have.
 *
 * Three things the copy does deliberately:
 *
 *  · It never says "deleted" unless something was. A visitor who has never
 *    talked to Prest0n, or whose thirty days already passed, is told there
 *    was nothing to remove. A reassuring fiction about a deletion that did
 *    not happen is the failure this whole workstream is about.
 *  · A partial wipe reads as partial. The server gives up after a bounded
 *    number of passes and the daily sweep finishes the job; saying "gone"
 *    when some of it remains would stop the person asking.
 *  · A failure says the conversation is STILL THERE and gives an address.
 *    That is the one message where a vague "something went wrong" leaves
 *    somebody believing they are done when they are not.
 *
 * No confirmation step, on purpose. Thirty days of guest chat is not a matter
 * file, the same conversation is re-created the moment they type again, and a
 * "are you sure?" in front of a privacy control is the pattern this programme
 * spent the week removing from the cancellation flow.
 */
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  forgetGuestConversation,
  type ForgetGuestResult,
} from '../../services/guestChat/client';

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin: 0.75rem 0 1rem;
`;

const Outcome = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--brand-text-muted);
  max-width: 42rem;
`;

const Problem = styled(Outcome)`
  color: #8a4b2a;
`;

export interface ForgetGuestConversationProps {
  /** Test seam. */
  forget?: typeof forgetGuestConversation;
}

/** What to say for each shape the server can return. */
export function outcomeCopy(result: ForgetGuestResult): { text: string; problem: boolean } {
  if (result.ok === true) {
    if ('forgotten' in result) {
      return {
        text: 'done — that conversation is gone from our side, along with anything Prest0n '
          + 'remembered from it. nothing here is kept about you now.',
        problem: false,
      };
    }
    if ('partial' in result) return { text: result.message, problem: false };
    return {
      text: 'there was nothing to forget — we are not holding a conversation from this '
        + 'connection.',
      problem: false,
    };
  }
  return {
    text: result.message
      ?? 'we could not remove it just now, and it has not been deleted. please try again, or '
        + 'email support@prest0.ai and we will do it by hand.',
    problem: true,
  };
}

export const ForgetGuestConversation: React.FC<ForgetGuestConversationProps> = ({
  forget = forgetGuestConversation,
}) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ForgetGuestResult | null>(null);

  const run = useCallback(async () => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await forget());
    } finally {
      setBusy(false);
    }
  }, [forget]);

  const copy = result ? outcomeCopy(result) : null;

  return (
    <>
      <Row>
        <button type="button" onClick={run} disabled={busy} data-testid="forget-guest-conversation">
          {busy ? 'removing it…' : 'forget it now'}
        </button>
      </Row>
      {copy && (
        copy.problem
          ? <Problem role="alert" data-testid="forget-guest-problem">{copy.text}</Problem>
          : <Outcome role="status" data-testid="forget-guest-outcome">{copy.text}</Outcome>
      )}
    </>
  );
};

export default ForgetGuestConversation;
